import React from "react";

import { filter, keyBy, keys, groupBy, sortBy, uniqBy } from "lodash";

import { Container, Row, Col, Form, Card, Navbar, Table, Button, Spinner, Modal } from "react-bootstrap";
import { Select, Checkbox, Empty } from "antd";

import { getData, getMoyens, getProches, createMoyen, deleteMoyen, setAuthToken } from "./api";
import { getCurrentSession, login, completeNewPassword, logout } from "./auth";

import "bootstrap/dist/css/bootstrap.min.css";
import "antd/dist/antd.css";
import "./App.sass";

class App extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			// Données de base
			communes: [],
			indexedCommunes: {},
			moyens: [],
			indexedMoyens: {},
			indexedMoyensByCommune: {},
			categories: [],
			indexedCategories: [],
			ready: false,

			// Filtres de recherche
			type: "jour",
			selectedMoyens: {},
			selectedDestination: null,

			// Distances et durées
			results: [],

			// Identification
			login: null,
			isAdmin: false,

			// Login modal
			showLogin: false,
			loginEmail: "",
			loginPassword: "",
			loginError: null,
			loginLoading: false,
			newPasswordRequired: false,
			newPassword: "",
			cognitoUser: null,
			cognitoUserAttributes: null,

			// Creation de nouveaux moyens
			selectedCategorie: null,
			selectedCommune: null
		};
	}

	async componentDidMount() {
		// Tente de récupérer une session Cognito existante
		const session = await getCurrentSession();
		if (session) {
			setAuthToken(session.token);
			const isAdmin = session.groups.includes("admin");
			this.setState({
				login: session.email,
				isAdmin
			});
		}

		this.fetchBaseData();
	}

	fetchBaseData() {
		getData().then(result => {
			this.setState({
				...result,
				categories: [
					...result.categories.map(c => {
						return {
							...c,
							key: c.id,
							value: c.id,
							label: c.nom + " (" + c.type + ")"
						};
					})
				],
				indexedCategories: keyBy(result.categories, c => c.id),
				communes: sortBy(
					result.communes.map(c => ({
						...c,
						key: c.id,
						value: c.id,
						label: c.nom
					})),
					c => c.nom
				),
				indexedCommunes: keyBy(result.communes, c => c.id),
				indexedMoyens: keyBy(result.moyens, c => c.id),
				indexedMoyensByCommune: groupBy(result.moyens, c => c.commune_id),
				ready: true
			});
		});
	}

	fetchMoyensData() {
		getMoyens().then(result => {
			this.setState({
				...result,
				indexedMoyens: keyBy(result.moyens, c => c.id),
				indexedMoyensByCommune: groupBy(result.moyens, c => c.commune_id)
			});
		});
	}

	handleSearch() {
		if (!this.state.selectedDestination) return;

		let sources = [];
		for (let m in this.state.selectedMoyens) {
			if (this.state.selectedMoyens[m] && this.state.indexedMoyens[m]) {
				sources.push(this.state.indexedMoyens[m].commune_id);
			}
		}

		if (sources.length === 0) return;

		this.setState({ results: [] });
		getProches(sources, this.state.selectedDestination).then(result => {
			this.setState({ results: sortBy(result.results, r => r.duree) });
		});
	}

	handleDeleteMoyen(moyenId) {
		if (window.confirm("Souhaitez-vous réellement supprimer cet élément? Cette action est irréversible.")) {
			deleteMoyen(moyenId).then(() => {
				this.fetchMoyensData();
			});
		}
	}

	handleReallyCreateMoyen() {
		createMoyen(this.state.selectedCategorie, this.state.selectedCommune).then(() => {
			this.fetchMoyensData();
		});
	}

	async handleLogin() {
		this.setState({ loginLoading: true, loginError: null });
		try {
			const result = await login(this.state.loginEmail, this.state.loginPassword);

			if (result.newPasswordRequired) {
				this.setState({
					newPasswordRequired: true,
					cognitoUser: result.user,
					cognitoUserAttributes: result.userAttributes,
					loginLoading: false
				});
				return;
			}

			setAuthToken(result.token);
			const isAdmin = result.groups.includes("admin");
			this.setState({
				login: result.email,
				isAdmin,
				showLogin: false,
				loginLoading: false,
				loginEmail: "",
				loginPassword: ""
			});
		} catch (err) {
			this.setState({ loginError: err.message, loginLoading: false });
		}
	}

	async handleNewPassword() {
		this.setState({ loginLoading: true, loginError: null });
		try {
			const result = await completeNewPassword(
				this.state.cognitoUser,
				this.state.newPassword,
				this.state.cognitoUserAttributes
			);

			setAuthToken(result.token);
			const isAdmin = result.groups.includes("admin");
			this.setState({
				login: result.email,
				isAdmin,
				showLogin: false,
				loginLoading: false,
				newPasswordRequired: false,
				loginEmail: "",
				loginPassword: "",
				newPassword: ""
			});
		} catch (err) {
			this.setState({ loginError: err.message, loginLoading: false });
		}
	}

	handleLogout() {
		logout();
		setAuthToken(null);
		this.setState({ login: null, isAdmin: false });
	}

	getTypesForCommune(communeId) {
		let moyensConcernes = filter(keys(this.state.selectedMoyens), m => {
			if (!this.state.indexedMoyens[m]) return false;
			return this.state.indexedMoyens[m].commune_id === communeId;
		});
		let categoriesConcernees = moyensConcernes.map(
			m => this.state.indexedCategories[this.state.indexedMoyens[m].categorie_id]
		);

		return uniqBy(categoriesConcernees, c => c.id)
			.map(c => c.nom)
			.join(" / ");
	}

	renderLoginModal() {
		return (
			<Modal show={this.state.showLogin} onHide={() => this.setState({ showLogin: false })}>
				<Modal.Header closeButton>
					<Modal.Title>Connexion administrateur</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{this.state.loginError && (
						<div className="alert alert-danger">{this.state.loginError}</div>
					)}
					{this.state.newPasswordRequired ? (
						<Form.Group>
							<Form.Label>Nouveau mot de passe</Form.Label>
							<Form.Control
								type="password"
								placeholder="Choisissez un nouveau mot de passe"
								value={this.state.newPassword}
								onChange={e => this.setState({ newPassword: e.target.value })}
								onKeyPress={e => e.key === "Enter" && this.handleNewPassword()}
							/>
							<Form.Text className="text-muted">
								Première connexion : veuillez choisir un nouveau mot de passe.
							</Form.Text>
						</Form.Group>
					) : (
						<>
							<Form.Group>
								<Form.Label>Email</Form.Label>
								<Form.Control
									type="email"
									placeholder="admin@example.com"
									value={this.state.loginEmail}
									onChange={e => this.setState({ loginEmail: e.target.value })}
								/>
							</Form.Group>
							<Form.Group>
								<Form.Label>Mot de passe</Form.Label>
								<Form.Control
									type="password"
									value={this.state.loginPassword}
									onChange={e => this.setState({ loginPassword: e.target.value })}
									onKeyPress={e => e.key === "Enter" && this.handleLogin()}
								/>
							</Form.Group>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					{this.state.newPasswordRequired ? (
						<Button
							variant="primary"
							disabled={this.state.loginLoading || !this.state.newPassword}
							onClick={() => this.handleNewPassword()}
						>
							{this.state.loginLoading ? "..." : "Valider"}
						</Button>
					) : (
						<Button
							variant="primary"
							disabled={this.state.loginLoading}
							onClick={() => this.handleLogin()}
						>
							{this.state.loginLoading ? "..." : "Connexion"}
						</Button>
					)}
				</Modal.Footer>
			</Modal>
		);
	}

	render() {
		return (
			<div className="App">
				<Navbar bg="dark" expand="lg">
					<Navbar.Brand href="#home">Aide à la décision d'engagement</Navbar.Brand>
					<div style={{ marginLeft: "auto" }}>
						{this.state.login ? (
							<>
								<span style={{ color: "#aaa", marginRight: 10 }}>{this.state.login}</span>
								<Button variant="outline-light" size="sm" onClick={() => this.handleLogout()}>
									Déconnexion
								</Button>
							</>
						) : (
							<Button variant="outline-light" size="sm" onClick={() => this.setState({ showLogin: true })}>
								Admin
							</Button>
						)}
					</div>
				</Navbar>
				{this.renderLoginModal()}
				<br />
				<Container fluid>
					<Row>
						{!this.state.ready ? (
							<div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
								<Spinner animation="border" role="status">
									<span className="sr-only">Chargement en cours...</span>
								</Spinner>
							</div>
						) : (
							<>
								<Col sm={4}>
									<Card>
										<Card.Body>
											<div className="type">
												<div>Jour</div>
												<Form.Check
													type="switch"
													id="custom-switch"
													onChange={v => {
														this.setState({
															type: this.state.type === "jour" ? "nuit" : "jour",
															results: [],
															selectedMoyens: {}
														});
													}}
													value={1}
													label={""}
												/>
												<div>Nuit</div>
											</div>

											{filter(this.state.categories, { type: this.state.type }).map(c => {
												return (
													<div className="category" key={c.id}>
														<div className="header">{c.nom}</div>

														<div className="body">
															{filter(
																sortBy(this.state.moyens, m => {
																	return this.state.indexedCommunes[m.commune_id].nom;
																}),
																m => {
																	return m.categorie_id === c.id;
																}
															).map(m => {
																return (
																	<div key={m.id} className="item">
																		<div>
																			{this.state.isAdmin ? (
																				<Button
																					variant="danger"
																					size="sm"
																					onClick={() => {
																						this.handleDeleteMoyen(m.id);
																					}}
																				>
																					-
																				</Button>
																			) : null}
																			{this.state.indexedCommunes[m.commune_id].nom}
																		</div>
																		<Checkbox
																			label=""
																			defaultValue={this.state.selectedMoyens[m.id]}
																			onChange={() => {
																				this.setState(
																					{
																						selectedMoyens: {
																							...this.state.selectedMoyens,
																							[m.id]: this.state.selectedMoyens[m.id]
																								? false
																								: true
																						}
																					},
																					() => {
																						this.handleSearch();
																					}
																				);
																			}}
																		/>
																	</div>
																);
															})}
														</div>
													</div>
												);
											})}
										</Card.Body>
									</Card>
									<br />

									{this.state.isAdmin ? (
										<Card>
											<Card.Header>Ajouter un moyen d'intervention</Card.Header>
											<Card.Body>
												<Select
													placeholder="Type"
													style={{ height: 40 }}
													showSearch
													onChange={selectedCategorie => {
														this.setState({ selectedCategorie });
													}}
													filterOption={(input, option) =>
														option.props.children
															.toLowerCase()
															.indexOf(input.toLowerCase()) >= 0
													}
												>
													{this.state.categories.map(d => (
														<Select.Option key={d.value}>{d.label}</Select.Option>
													))}
												</Select>
												<Select
													placeholder="Lieu associé à ce moyen d'intervention"
													style={{ height: 40 }}
													showSearch
													onChange={selectedCommune => {
														this.setState({ selectedCommune });
													}}
													filterOption={(input, option) =>
														option.props.children
															.toLowerCase()
															.indexOf(input.toLowerCase()) >= 0
													}
												>
													{this.state.communes.map(d => (
														<Select.Option key={d.value}>{d.label}</Select.Option>
													))}
												</Select>
											</Card.Body>
											<Card.Footer>
												<Button
													variant="warning"
													size="sm"
													disabled={
														!this.state.selectedCategorie || !this.state.selectedCommune
													}
													onClick={() => {
														this.handleReallyCreateMoyen();
														this.setState({
															showCreateModal: false,
															selectedCommune: null,
															selectedCategorie: null
														});
													}}
												>
													Valider
												</Button>
											</Card.Footer>
										</Card>
									) : null}
								</Col>
								<Col>
									<Select
										placeholder="Lieu d'intervention"
										style={{ height: 40 }}
										showSearch
										onChange={selectedDestination => {
											this.setState({ selectedDestination }, () => {
												this.handleSearch();
											});
										}}
										filterOption={(input, option) =>
											option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
										}
									>
										{this.state.communes.map(d => (
											<Select.Option key={d.value}>{d.label}</Select.Option>
										))}
									</Select>
									<br />
									<br />
									<Table>
										<thead>
											<tr>
												<th>Moyen(s) engagé(s)</th>
												<th>Ville de départ</th>
												<th>Durée</th>
												<th>Distance</th>
											</tr>
										</thead>
										<tbody>
											{this.state.results.map(r => {
												return (
													<tr key={r.source}>
														<td>{this.getTypesForCommune(r.source)}</td>
														<td>{this.state.indexedCommunes[r.source].nom}</td>
														<td>
															{(r.duree / 60).toLocaleString("fr-FR", {
																maximumFractionDigits: 0
															})}{" "}
															minutes
														</td>
														<td>
															{(r.distance / 1000).toLocaleString("fr-FR", {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2
															})}{" "}
															kms
														</td>
													</tr>
												);
											})}
											{this.state.results.length === 0 ? (
												<tr>
													<td colSpan={4}>
														<Empty
															description="Aucun résultat. Veuillez sélectionner des moyens d'intervention dans la partie gauche et un lieu d'intervention ci-dessus."
															image={Empty.PRESENTED_IMAGE_SIMPLE}
														/>
													</td>
												</tr>
											) : null}
										</tbody>
									</Table>
								</Col>
							</>
						)}
					</Row>
				</Container>
			</div>
		);
	}
}

export default App;
